import { useState, type FormEvent } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/auth-context.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';

export function LoginPage(): JSX.Element {
  const { state, login, submitTotp, submitRecovery } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isChallenge = state.kind === 'challenge';
  const errorMessage =
    state.kind !== 'authenticated' && state.kind !== 'loading' ? state.error : undefined;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isChallenge) {
        if (recoveryMode) await submitRecovery(code.trim());
        else await submitTotp(code.trim());
      } else {
        await login(email.trim().toLowerCase(), password);
      }
    } catch {
      // ошибка уже в state.error через AuthProvider
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg border border-border bg-bg-soft p-6 shadow-sm"
      >
        <div className="mb-6 flex items-center gap-2">
          {isChallenge ? (
            <ShieldCheck className="h-5 w-5 text-accent" />
          ) : (
            <Lock className="h-5 w-5 text-accent" />
          )}
          <h1 className="text-lg font-semibold tracking-tight">
            {isChallenge ? 'Двухфакторный код' : 'Вход в Tracker'}
          </h1>
        </div>

        {!isChallenge ? (
          <>
            <div className="mb-4 space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="mb-5 space-y-1.5">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </>
        ) : (
          <>
            <p className="mb-4 text-sm text-fg-muted">
              {recoveryMode
                ? 'Введи один из 8 recovery-кодов из менеджера паролей.'
                : 'Открой 2FA-приложение и введи 6-значный код.'}
            </p>
            <div className="mb-4 space-y-1.5">
              <Label htmlFor="code">{recoveryMode ? 'Recovery-код' : 'Код'}</Label>
              <Input
                id="code"
                inputMode={recoveryMode ? 'text' : 'numeric'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={recoveryMode ? 'AAAA-BBBB-CCCC' : '000000'}
                required
                autoFocus
                className="font-mono tracking-wider"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setCode('');
                setRecoveryMode((v) => !v);
              }}
              className="mb-5 text-xs text-fg-muted hover:text-accent"
            >
              {recoveryMode ? '← Назад к коду из приложения' : 'Использовать recovery-код'}
            </button>
          </>
        )}

        {errorMessage ? (
          <p className="mb-4 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
            {errorMessage}
          </p>
        ) : null}

        <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
          {submitting ? '…' : isChallenge ? 'Подтвердить' : 'Войти'}
        </Button>
      </form>
    </div>
  );
}
